import { Menu, Transition } from '@headlessui/react';
import {
    Cog6ToothIcon,
    CogIcon,
    HomeIcon,
    SparklesIcon,
    TableCellsIcon,
} from '@heroicons/react/24/outline';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Fragment } from 'react';

import { signOutUser } from '../services/authService';
import { profilePictureUrl, user } from '../state/user';
import { Link } from './CustomRouter';
import KnockWidget from './KnockFeedWidget';
import OrganizationSwitcher from './OrganizationSwitcher';

// These types probably need to live somewhere else but this is fine for now
// Got them from stack: https://stackoverflow.com/a/76042282/2446724
type IconSVGProps = React.PropsWithoutRef<React.SVGProps<SVGSVGElement>> &
    React.RefAttributes<SVGSVGElement>;
type IconProps = IconSVGProps & {
    title?: string;
    titleId?: string;
};

interface NavItem {
    name: string;
    href: string;
    icon: React.FC<IconProps>;
}

function Nav() {
    // Define all available navigation items
    const allNavItems = {
        worksheets: { name: 'Worksheets', href: '/', icon: TableCellsIcon },
        pillar: { name: 'Pillar', href: '/pillar', icon: HomeIcon },
        lexi: { name: 'AI Audit', href: '/lexi', icon: SparklesIcon },
        utilities: { name: 'Utilities', href: '/utilities', icon: CogIcon },
        settings: { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
    };

    // Build navigation list - show all items by default
    // Feature flags can be used to hide specific items if needed
    const navigations: NavItem[] = Object.values(allNavItems);

    const userNavigation = [{ name: 'Sign out', href: '#', action: handleSignOut }];

    function classNames(...classes: string[]) {
        return classes.filter(Boolean).join(' ');
    }

    async function handleSignOut(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
        e.preventDefault();
        signOutUser();
    }

    return (
        <div className="fixed inset-y-0 rounded-lg m-4 z-10 flex flex-col w-64 bg-white shadow">
            {/* Header with Profile */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <img
                        className="h-10 w-10 rounded-full ring-2 ring-indigo-100"
                        src={profilePictureUrl.value}
                        alt={`${user.value.firstName || '?'} ${user.value.lastName || '?'}`}
                    />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                            {user.value.firstName} {user.value.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                            {user.value.email}
                        </p>
                    </div>
                </div>
            </div>

            {/* Organization Switcher - renders its own container or null */}
            <OrganizationSwitcher />

            {/* Navigation Menu */}
            <nav className="flex-1 px-3 py-4 overflow-y-auto">
                <div className="space-y-1">
                    {navigations.map((item) => (
                        <Link
                            key={item.name}
                            to={item.href}
                            className="[&.active]:bg-indigo-50 [&.active]:text-indigo-600 [&.active]:border-indigo-600 group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg text-gray-700 hover:text-indigo-600 hover:bg-gray-50 transition-colors duration-150 border-l-4 border-transparent"
                        >
                            <item.icon
                                className="h-5 w-5 shrink-0 [.active_&]:text-indigo-600"
                                aria-hidden="true"
                            />
                            <span>{item.name}</span>
                        </Link>
                    ))}
                </div>

                {/* Notifications Widget */}
                {useFeatureFlagEnabled(`settings-feature`) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <KnockWidget />
                    </div>
                )}
            </nav>

            {/* Footer with User Menu */}
            <div className="border-t border-gray-200">
                <Menu as="div" className="relative">
                    <Menu.Button className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150">
                        <Cog6ToothIcon className="h-5 w-5 text-gray-400" />
                        <span className="flex-1 text-left">Account</span>
                        <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute bottom-full left-4 right-4 mb-2 origin-bottom rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {userNavigation.map((item) => (
                                <Menu.Item key={item.name}>
                                    {({ active }) => (
                                        <a
                                            href={item.href}
                                            onClick={handleSignOut}
                                            className={classNames(
                                                active ? 'bg-gray-100' : '',
                                                'flex items-center gap-2 px-4 py-2 text-sm text-gray-700',
                                            )}
                                        >
                                            <svg
                                                className="h-4 w-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                                />
                                            </svg>
                                            {item.name}
                                        </a>
                                    )}
                                </Menu.Item>
                            ))}
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
        </div>
    );
}

export default Nav;
